import { useCallback, useEffect, useState } from 'react'
import {
  ACCOUNT_ACCESS_TOKENS,
  ACCOUNT_ACCESS_TOKENS_EU,
  ACCOUNT_ACCESS_TOKENS_US
} from '../consts'
import { AccessTokenResponse } from '../oauth'
import { getShopLocale } from '@helpers/env'

export interface AuthTokens {
  accessToken: string
  refreshToken: string
  idToken: string | null
  tokenExpiry: number
}

export const getTokensStorageLocation = () => {
  switch (getShopLocale()) {
    case 'us':
      return ACCOUNT_ACCESS_TOKENS_US
    case 'eu':
      return ACCOUNT_ACCESS_TOKENS_EU
    default:
      return ACCOUNT_ACCESS_TOKENS
  }
}

export function useAuthTokens() {
  const [token, setToken] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState<string | null>(null)
  const [tokenExpiry, setTokenExpiry] = useState<number | null>(null)
  const [idToken, setIdToken] = useState<string | null>(null)

  const resetCredentials = useCallback(() => {
    console.log('Resetting credentials', new Date().toISOString())
    localStorage.removeItem(getTokensStorageLocation())
    setToken(null)
    setRefreshToken(null)
    setTokenExpiry(null)
    setIdToken(null)
  }, [])

  const storeTokens = (tokens: AccessTokenResponse) => {
    const { access_token, expires_in, id_token, refresh_token } = tokens

    const tokenExpiry = Date.now() + expires_in * 1000

    // We only get idToken on initial token exchange (not on refresh) so we keep track of latest ...
    let latestIdToken = idToken

    // ... and only use id_token if it was received in response
    if (id_token) {
      latestIdToken = id_token
    }

    const authTokens: AuthTokens = {
      accessToken: access_token,
      refreshToken: refresh_token,
      idToken: latestIdToken,
      tokenExpiry
    }

    localStorage.setItem(getTokensStorageLocation(), JSON.stringify(authTokens))

    setToken(access_token)
    setRefreshToken(refresh_token)
    setTokenExpiry(tokenExpiry)
    setIdToken(latestIdToken)
  }

  useEffect(() => {
    const accessTokens = localStorage.getItem(getTokensStorageLocation())

    if (accessTokens) {
      const tokens = JSON.parse(accessTokens) as AuthTokens
      setToken(tokens.accessToken)
      setRefreshToken(tokens.refreshToken)
      setTokenExpiry(tokens.tokenExpiry)
      setIdToken(tokens.idToken)
    }
  }, [])

  return {
    token,
    refreshToken,
    tokenExpiry,
    idToken,
    resetCredentials,
    storeTokens
  }
}
